import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FlexRow } from '@app/flex-framework/flex-api.model';
import { FlexRowReorderService } from '@app/flex-framework/services/flex-row-reorder.service';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { TargetType } from '@app/profile/profile-api.model';
import { LinkRowLink } from '@app/shared/components/link-row/link-row.model';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { Target } from '@app/target/target-api.model';
import { DfFormFieldBuilder, DfFormFieldConfig } from '@lib/fresco';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';

interface LocalModel {
  name: string;
  description: string;
}

/**
 * @param isEditing whether we're editing the page or not
 * @param hideLinks removes links even if links param is configured (DEPRECATED: No links in the new header.)
 * @param label becomes the h2 heading in the section
 * @param links usually this ends up being a "View All" link but anything can be used here. If there is no url but does contain onClick it will be treated like a button for a11y
 * @param markdownDescription the rows description, in markdown
 * @param menuConfig config object similar to actionOptions for populating the meatball menu
 * @param isEditing removes meatball menu even if menuConfig param is configured
 */
@Component({
  selector: 'dgx-flex-row-header',
  styleUrls: ['./flex-row-header.component.scss'],
  templateUrl: './flex-row-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexRowHeaderComponent
  extends SubscriberBaseDirective
  implements OnChanges, OnInit
{
  public headerLabel: string;
  public i18n = this.translate.instant([
    'Core_Back',
    'Core_Done',
    'dgFlexRow_EditSectionTitle',
    'dgFlexRow_EditSectionDetails',
    'dgFlexRow_EditSectionTitlePlaceholder', // Add a Title
    'dgFlexRow_EditSectionDescription',
    'dgFlexRow_EditSectionDescriptionPlaceholder', // Add a Description
    'dgFlexRow_ReorderItems',
  ]);
  public form: FormGroup = new FormGroup({});
  public fields: DfFormFieldConfig[] = [];
  public model: LocalModel = {
    name: undefined,
    description: undefined,
  };
  public options: FormlyFormOptions = {};
  public prevName: string;
  public prevDescription: string;
  public reorderMenuConfig: MenuViewModel[] = [];
  public editMenuConfig: MenuViewModel[] = [];
  public isEditing: boolean = false;
  public isReordering: boolean;
  public targetType: TargetType;

  /** @deprecated: No links in the new header. Buttons instead. View all controlled with a footer button. */
  @Input() public hideLinks: boolean = false;
  @Input() public links: LinkRowLink[] = [];
  @Input() public markdownDescription: string;
  // TODO: Convert menuConfig to take only one menu item (edit) once Opportunities and Skill Standards are
  // no longer using the old carousels.
  @Input() public menuConfig: MenuViewModel[] = [];
  @Input() public row: FlexRow;
  @Input() public hideReorderButton: boolean = false;
  @Input() public hideQuickEditHeader: boolean = false;
  @Input() public useCarousels = false;
  @Input() public permanentEditMode = false;

  @Output() public editRow = new EventEmitter<FlexRow>();
  @Output() public toggleReorderRow = new EventEmitter<void>();

  constructor(
    private activeRoute: ActivatedRoute,
    private builder: DfFormFieldBuilder,
    private cdr: ChangeDetectorRef,
    private flexRowService: FlexRowService,
    private router: Router,
    private translate: TranslateService,
    private flexRowReorderService: FlexRowReorderService
  ) {
    super();
  }

  public get showReorderButton(): boolean {
    const resourceType = this.row.section.resourceType;
    const hasMultipleResources = this.row.targetResources?.length > 1;

    return (
      hasMultipleResources &&
      !this.hideReorderButton &&
      !this.useCarousels &&
      this.isEditing &&
      // Show for all except these types.
      resourceType !== 'LinkText' &&
      resourceType !== 'FreeformText' &&
      resourceType !== 'Opportunity'
    );
  }

  public get isReorderingPage(): boolean {
    return this.router.url.indexOf('reorder') > -1;
  }

  public get headerText(): string {
    return this.isReordering
      ? this.i18n.dgFlexRow_ReorderItems
      : this.row.section.name;
  }

  public get showQuickEditHeader(): boolean {
    const resourceType = this.row.section.resourceType;
    return (
      !this.useCarousels &&
      !this.hideQuickEditHeader &&
      !this.isReorderingPage &&
      this.isEditing &&
      // Show for all except these types.
      resourceType !== 'LinkText' &&
      resourceType !== 'FreeformText' &&
      resourceType !== 'Opportunity'
    );
  }

  public ngOnInit(): void {
    // Grab target type from snapshot.
    this.targetType =
      this.activeRoute.snapshot.data?.response?.target.targetType;

    // When in Permanent Edit Mode, there's no need to subscribe to query params.
    if (this.permanentEditMode) {
      this.isEditing = true;
      if (this.showQuickEditHeader) {
        this.setupInLineEditing();
      }
      return;
    }

    // Otherwise, listen for changes to our query params.
    this.activeRoute.queryParams
      ?.pipe(this.takeUntilDestroyed())
      .subscribe((params) => {
        // Set our editing mode
        this.isEditing = params.editMode === 'true';
        // If quick edit header is showing *and* our fields array is empty, build our form
        if (this.showQuickEditHeader && !this.fields.length) {
          this.setupInLineEditing();
        }
        // Detect changes both in this component and in children.
        this.cdr.detectChanges();
      });

    this.flexRowReorderService.isReorderModeActive$
      .pipe(this.takeUntilDestroyed())
      .subscribe(
        (isReorderMode: boolean) => (this.isReordering = isReorderMode)
      );
  }

  public ngOnChanges({ row }: SimpleChanges): void {
    const modelIsSet = !!this.model.name || !!this.model.description;
    if (
      !this.isEditing ||
      !row ||
      !modelIsSet ||
      (row.currentValue.section.name === this.model.name &&
        row.currentValue.section.description === this.model.description)
    ) {
      return;
    }
    // Listen for updates from *outside* of this component to keep
    // our model updated with changes from edit modals.
    // grab the section
    const { section } = row.currentValue;
    // name updates?
    if (section.name !== this.model.name && this.prevName === this.model.name) {
      this.form.patchValue({ name: section.name });
      this.prevName = section.name;
    }
    // description updates?
    if (
      section.description !== this.model.description &&
      this.prevDescription === this.model.description
    ) {
      this.form.patchValue({ description: section.description });
      this.prevDescription = section.description;
    }
  }

  public toggleReorder() {
    this.flexRowReorderService.toggleReorderMode(!this.isReordering);
    this.cdr.detectChanges();
    this.cdr.markForCheck();
    this.toggleReorderRow.emit();
  }

  public updateSection(
    value: string,
    model: LocalModel,
    formControl: AbstractControl
  ) {
    // Trim values before checking them.
    value = (value ? value : '').trim();
    model.name = (model.name ? model.name : '').trim();
    model.description = (model.description ? model.description : '').trim();
    // Don't do anything with invalid changes
    if (
      formControl.invalid ||
      // If the name is what we're updating and the name matches the saved name...
      (value === model.name && value === this.row.section.name) ||
      // ...or the description is what we're updating and it matches the saved description...
      (value === model.description && value === this.row.section.description)
    ) {
      return;
    }

    // Otherwise, update
    this.flexRowService
      .updateSection(
        {
          ...model,
          parentNode: this.row.section.parentNode,
          targetId: this.row.section.targetId,
        },
        this.targetType
      )
      .pipe(tap(() => this.flexRowService.rowUpdate()))
      .subscribe(() => {
        // Update a *child* of our @Input() to avoid additional change detection triggers.
        this.row.section = {
          ...this.row.section,
          name: model.name,
          description: model.description,
        };
        // Save our new values to our prev properties.
        this.prevName = model.name;
        this.prevDescription = model.description;
        this.editRow.emit(this.row);
      });
  }

  private setupInLineEditing() {
    // Ensure row has a defined name property
    this.row.section.name =
      this.row.section.name ?? this.row.sectionDefaultTitle;
    // Initialize previous name and description for comparisons to keep our fields in sync
    // with changes made from edit modals.
    this.prevName = this.row.section.name?.trim();
    this.prevDescription = this.row.section.description?.trim();
    // Initiate the model values
    this.model.name = this.prevName;
    this.model.description = this.prevDescription;

    // Build the fields
    this.fields = [
      this.builder
        .requiredTextInput('name', 'dgFlexRow_EditSectionTitle')
        .withMaxLength(255)
        .onBlur<LocalModel>((_, value, formControl) =>
          this.updateSection(value.name, this.model, formControl)
        )
        .withPlaceholder(this.i18n.dgFlexRow_EditSectionTitlePlaceholder)
        .withDgatId('flex-row-header-5ba')
        .build(),
      this.builder
        .charCountedTextarea(
          'description',
          'dgFlexRow_EditSectionDescription',
          2000 // the DB has a char limit of 2,000 on this description.
        )
        .updatedOn('change') // updated on change (blur is default) in order to update our *validation* message
        .onBlur<LocalModel>((_, value, formControl) =>
          this.updateSection(value.description, this.model, formControl)
        )
        .withPlaceholder(this.i18n.dgFlexRow_EditSectionDescriptionPlaceholder)
        .withDgatId('flex-row-header-15e')
        .styledBy('textarea--extra-short')
        .build(),
    ];
    // View update
    this.cdr.detectChanges();
  }
}
